import { RotateCcw } from 'lucide-react';
import { Button, Input, Label } from '@/shared/components';
import type { PropertyPortalBrandingResponse } from '@/shared/api';
import { PortalLogoField } from './PortalLogoField';

interface Props {
  branding: PropertyPortalBrandingResponse;
  brandName: string;
  editable: boolean;
  dirty: boolean;
  busy: boolean;
  error: string | null;
  conflict: boolean;
  onBrandNameChange: (value: string) => void;
  onSave: () => void;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onReload: () => void;
}

// Wo die Marke erscheint — und wo bewusst nicht (PO-Entscheid Sprint 65: nicht in E-Mail und OTA).
const REACH = ['Onboarding', 'Zahlung', 'Check-in', 'Lade- und Fehlerseiten'];

/** Die Marke des Objekts im Portal: optionaler Name und Logo, gültig für alle Apartments. */
export function PortalBrandingEditor(props: Props) {
  const own = !!props.branding.brandName?.trim() || props.branding.hasLogo;
  return (
    <section
      aria-labelledby="portal-brand-title"
      className="border-line/50 bg-card shadow-card rounded-lg border"
    >
      <div className="border-line/60 flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
        <h3 id="portal-brand-title" className="text-ink font-serif text-lg">
          Marke
        </h3>
        <span
          className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${own ? 'bg-sage/10 text-sage' : 'bg-card-alt text-ink-soft'}`}
        >
          {own ? 'Eigene Marke' : 'StayOn-Standard'}
        </span>
      </div>
      <div className="space-y-5 px-5 py-4">
        {props.editable ? (
          <div>
            <Label htmlFor="portal-brand-name">Markenname</Label>
            <Input
              id="portal-brand-name"
              value={props.brandName}
              maxLength={120}
              placeholder="StayOn"
              disabled={props.busy}
              onChange={(event) => props.onBrandNameChange(event.target.value)}
            />
            <p className="text-ink-soft mt-1.5 text-xs">
              Erscheint im Kopf des Gastportals. Leer lassen, dann steht dort „StayOn“.
            </p>
          </div>
        ) : (
          <dl>
            <dt className="text-ink-soft text-xs">Markenname</dt>
            <dd className="text-ink mt-1 text-sm font-semibold">
              {props.branding.brandName || 'StayOn-Standard'}
            </dd>
          </dl>
        )}
        <PortalLogoField
          branding={props.branding}
          editable={props.editable}
          busy={props.busy}
          onUpload={props.onUpload}
          onRemove={props.onRemove}
        />
        {props.error && (
          <div
            role="alert"
            className="border-sealwax/30 bg-sealwax/5 text-sealwax rounded-md border p-3 text-sm"
          >
            {props.error}
            {props.conflict && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="min-h-11"
                leftIcon={<RotateCcw className="size-4" />}
                onClick={props.onReload}
              >
                Aktuellen Stand laden
              </Button>
            )}
          </div>
        )}
        {props.editable && (
          <div className="flex justify-end">
            <Button
              type="button"
              variant="brand"
              className="min-h-11"
              loading={props.busy}
              disabled={!props.dirty}
              onClick={props.onSave}
            >
              Branding speichern
            </Button>
          </div>
        )}
      </div>
      <div className="border-line/60 border-t px-5 py-4">
        <p className="flex flex-wrap items-center gap-2">
          <span className="text-ink-soft mr-1 text-[11px] font-semibold uppercase tracking-[0.08em]">
            Wirkt auf
          </span>
          {REACH.map((place) => (
            <span
              key={place}
              className="border-line text-ink rounded-full border px-2.5 py-0.5 text-xs"
            >
              {place}
            </span>
          ))}
        </p>
        <p className="text-ink-soft mt-2 text-xs">
          Nicht in E-Mails und OTA-Nachrichten. Ohne sicher erkannte Buchung zeigt das Portal immer
          StayOn.
        </p>
      </div>
    </section>
  );
}
